import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BeeScorecardLicense, BeeScorecardLicenseSearchRequest } from '../../../../Component/Model/Dto/ScorecardLicense';
import { BEEScorecardService } from '../../../../Component/Api/BEEScorecard/BEEScorecard';
import { CoreStaticDataService } from '../../../../Component/Api/StaticData/CoreStaticDataService';
import { createScorecardService } from '../../../../Component/Api/VerificationAgency/redirectCreateScorecard/CreateScorecardService';
import { createScorecardExternalRedirect, createConsolidatedScorecardExternalRedirect } from '../../../../Component/Api/VerificationAgency/redirectCreateScorecard/ApiUrl.CreateScorecard';
import config from '../../../../config';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { IFilter, FilterType } from '../../../../Component/Common/Components/DataTable/Filters';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { GenericDataModel } from '../../../../Component/Model/Dto/StaticData';
import { EntriesPerPage } from '../../../../Component/Model/Table/Table';
import { getAllScorecardTypesExceptEE } from '../../../../Helpers/scorecardHelper';
import { defaultObject, emptyGuid } from '../../../../Helpers/typescriptHelper';
import { ScorecardLicenseModalSummaryTableMapper } from './ScorecardLicenseModalTableColumns';
import { ColumnSortOption } from '../../../../Component/Model/Dto/ColumnSortOption';
import { ScorecardTypes } from '../../../../Component/Model/Enum/BEE123Enums';

interface IProps {
    modalDescription: String;
    isGroup?: boolean;
}

export const ScorecardLicenseModal = (props: IProps) => {
    const [scorecardSummaryData, setScorecardSummaryData] = useState<BeeScorecardLicense[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
    const [beeScorecardType, setBeeScorecardType] = useState<GenericDataModel[]>(defaultObject);
    const [searchValue, setSearchValue] = useState<string>("");
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(1);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(1);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const history = useHistory();
    const useIframePages = config.enable_iframe_pages === 'true';

    const [filters, setFilters] = useState({});

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const GetScorecardType = async () => {
        const scorecardType = await CoreStaticDataService.getBeeScorecardType();
        const scorecardTypeWithoutEE = scorecardType.data.filter((data: any) => data.id !== ScorecardTypes.Employment_Equity.toString());
        setBeeScorecardType(scorecardTypeWithoutEE.map((data: any) => {
            return { key: data.name, val: data.id };
        }));
    };

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const setSearchValueChanged = (searchValue: string) => {
        setSearchValue(searchValue);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let scorecardLicenseSearchRequest: BeeScorecardLicenseSearchRequest = {
                isGroup: props.isGroup ?? false,
                pageNumber: pageNumber,
                pageSize: entriesPerPage.defaultValue!,
                registrationId: emptyGuid,
                searchValue: searchValue,
                scorecardTypeIds: getAllScorecardTypesExceptEE(),
                columnSortOptions: columnSortOptions
            };
            scorecardLicenseSearchRequest = { ...scorecardLicenseSearchRequest, ...filters };
            const scorecardSummaryResult = await BEEScorecardService.getBeeScorecardLicenseSummaryData(scorecardLicenseSearchRequest);
            setScorecardSummaryData(scorecardSummaryResult.data.results);
            setFilteredItemsCount(scorecardSummaryResult.data.filteredItemsCount);
            setTotalItemsCount(scorecardSummaryResult.data.totalItemsCount);

        } catch (error) {
            console.log(error);
            setScorecardSummaryData(defaultObject);
            setFilteredItemsCount(1);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);
        }
    }

    const handleCreateScorecard = (id: string, isConsolidated: boolean) => {
        if (isConsolidated) {
            const navigate = useIframePages
                ? history.push
                : () => { window.location.href = `${createConsolidatedScorecardExternalRedirect}${id}`; };
            createScorecardService.CreateConsolidatedScorecard(id, navigate);
        }
        else if(props.isGroup === undefined)
        {
            const navigate = useIframePages
                ? history.push
                : () => { window.location.href = `${createScorecardExternalRedirect}${id}`; };
            createScorecardService.CreateScorecard(id, navigate);
        }
        else {
            createScorecardService.CreateGroupModule(id);
        }
    }

    const filtersChanged = async (filterProps: any) => {
        setPageNumber(1);
        let scorecardTypes = filterProps.scorecardTypes.map((type: string) => parseInt(type, 10));
        let isConsolidated =
            filterProps["isConsolidated"].length === 0
                ? null
                : filterProps["isConsolidated"];

        const updatedFilters: any = { ...filterProps, scorecardTypes };
        if (isConsolidated !== null) {
            updatedFilters.isConsolidated = isConsolidated;
        } else {
            delete updatedFilters.isConsolidated;
        }

        if (updatedFilters.scorecardTypes.length === 0) {
            updatedFilters.scorecardTypes = getAllScorecardTypesExceptEE();
        }

        setFilters(updatedFilters);
    };

    const scorecardLicensesAddFilterData: Array<IFilter> = [
        {
            label: "Scorecard Type:",
            values: beeScorecardType,
            type: FilterType.multiselect,
            propname: "scorecardTypes",
        },
        {
            label: "Is Consolidated:",
            values: [
                { key: "Yes", val: true },
                { key: "No", val: false },
            ],
            type: FilterType.select,
            propname: "isConsolidated",
        }
    ];

    useEffect(() => {
        GetScorecardType();
    }, []);

    useEffect(() => {
        fetchData();

    }, [entriesPerPage,
        pageNumber,
        searchValue,
        columnSortOptions,
        filters
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    const tableData = ScorecardLicenseModalSummaryTableMapper(scorecardSummaryData, handleCreateScorecard, props.isGroup as boolean);
    return (
        <>
            <MDTypography>{props.modalDescription}</MDTypography>
            <DataTable
                key={totalItemsCount}
                table={tableData!}
                canSearch
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                columnSortingChanged={columnSortingChanged}
                isLoading={isLoading}
                searchValueChanged={setSearchValueChanged}
                filters={scorecardLicensesAddFilterData}
                filtersChanged={filtersChanged}
                manualSorting={true}
            ></DataTable>
        </>
    )
}
